import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { can, canRemoveMember } from '@racedash/shared'
import type { Role } from '@racedash/shared'
import { useShell } from '../AppShell'
import { useMembers, usePendingInvites } from '../org/useMembers'
import { createInvite, inviteUrl, removeMember, revokeInvite, setMemberRole } from '../org/members'
import { relativeTime } from '../lib/time'
import { leaveOrganisation, renameOrganisation } from '../org/api'
import OrgNav from '../components/OrgNav'

const ROLES: Role[] = ['owner', 'editor', 'viewer']

function roleColour(role: Role): string {
  if (role === 'owner') return 'racedash'
  if (role === 'editor') return 'blue'
  return 'gray'
}

export default function Members() {
  const { user, currentOrg, role } = useShell()
  const { members, error } = useMembers(currentOrg?.id)
  const mayManage = can(role, 'member.invite')
  const invites = usePendingInvites(currentOrg?.id, mayManage)

  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('editor')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [orgName, setOrgName] = useState('')
  const navigate = useNavigate()

  if (!currentOrg) return <Text c="dimmed">Organisation not found.</Text>

  async function submitInvite(event: React.FormEvent) {
    event.preventDefault()
    if (!currentOrg || busy || !email.trim()) return
    setBusy(true)
    try {
      await createInvite(currentOrg.id, currentOrg.name, email, inviteRole, user.uid)
      setEmail('')
    } finally {
      setBusy(false)
    }
  }

  async function copy(token: string) {
    await navigator.clipboard.writeText(inviteUrl(token))
    setCopied(token)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1} size="h2">
            People
          </Title>
          <Text c="dimmed" size="sm">
            {currentOrg.kind === 'personal' ? 'Personal' : currentOrg.name}
          </Text>
        </div>
        <Button component={Link} to={`/o/${currentOrg.id}`} variant="default">
          Back
        </Button>
      </Group>

      <OrgNav orgId={currentOrg.id} role={role} />

      {error && <Alert color="red">{error}</Alert>}
      {!members && !error && <Text c="dimmed">Loading…</Text>}

      {members && (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={640}>
            <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Person</Table.Th>
                  <Table.Th w={150}>Joined</Table.Th>
                  <Table.Th w={150}>Role</Table.Th>
                  <Table.Th w={100} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {members.map((m) => {
                  const isPrimary = m.id === currentOrg.primaryOwnerUid
                  return (
                    <Table.Tr key={m.id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {m.displayName ?? m.email ?? m.id}
                          {m.id === user.uid && (
                            <Text span c="dimmed" fw={400}>
                              {' '}
                              — you
                            </Text>
                          )}
                        </Text>
                        {m.email && (
                          <Text size="xs" c="dimmed">
                            {m.email}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {relativeTime(m.joinedAt)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          {/* The primary owner's role is not editable: the
                              organisation must always have a responsible
                              party, so it cannot be demoted from here. */}
                          {mayManage && !isPrimary ? (
                            <Select
                              size="xs"
                              w={100}
                              data={ROLES}
                              value={m.role}
                              allowDeselect={false}
                              onChange={(v) =>
                                v && void setMemberRole(currentOrg.id, m.id, v as Role)
                              }
                            />
                          ) : (
                            <Badge variant="light" size="sm" color={roleColour(m.role)}>
                              {m.role}
                            </Badge>
                          )}
                          {isPrimary && (
                            <Badge variant="outline" size="sm" color="gray">
                              primary
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td align="right">
                        {canRemoveMember(role, currentOrg, m.id) && (
                          <Button
                            variant="subtle"
                            color="red"
                            size="compact-sm"
                            onClick={() => void removeMember(currentOrg.id, m.id)}
                          >
                            Remove
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}

      {mayManage && (
        <Paper withBorder p="lg">
          <Title order={2} size="h5">
            Invite someone
          </Title>
          <Text c="dimmed" size="sm" mb="md">
            The invite link is what admits them, not the address — so it works even if they sign in
            with a different one. Each link admits exactly one person and expires after 14 days.
          </Text>

          <form onSubmit={(e) => void submitInvite(e)}>
            <Group align="flex-end">
              <TextInput
                style={{ flex: 1 }}
                label="Email"
                type="email"
                required
                placeholder="their@email.com"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              <Select
                label="Role"
                w={130}
                data={ROLES}
                value={inviteRole}
                allowDeselect={false}
                onChange={(v) => setInviteRole((v as Role) ?? 'editor')}
              />
              <Button type="submit" loading={busy} disabled={!email.trim()}>
                Create invite
              </Button>
            </Group>
          </form>

          {invites?.length === 0 && (
            <Text c="dimmed" size="sm" mt="md">
              No pending invites.
            </Text>
          )}

          {invites && invites.length > 0 && (
            <Stack gap="xs" mt="md">
              {invites.map((i) => (
                <Paper withBorder p="sm" key={i.token}>
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <div style={{ minWidth: 0 }}>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {i.email}
                        </Text>
                        <Badge variant="light" size="sm" color={roleColour(i.role)}>
                          {i.role}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          expires {relativeTime(i.expiresAt)}
                        </Text>
                      </Group>
                      <Code block mt={6} style={{ fontSize: 11, overflowWrap: 'anywhere' }}>
                        {inviteUrl(i.token)}
                      </Code>
                    </div>
                    <Group gap={4} wrap="nowrap">
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        onClick={() => void copy(i.token)}
                      >
                        {copied === i.token ? 'Copied' : 'Copy'}
                      </Button>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-sm"
                        onClick={() => void revokeInvite(i.token)}
                      >
                        Revoke
                      </Button>
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      <Paper withBorder p="lg">
        <Title order={2} size="h5" mb="md">
          Organisation
        </Title>

        <Stack gap="md">
          {can(role, 'org.rename') && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (currentOrg && orgName.trim()) {
                  void renameOrganisation(currentOrg.id, orgName).then(() => setOrgName(''))
                }
              }}
            >
              <Group align="flex-end">
                <TextInput
                  style={{ flex: 1 }}
                  label="Name"
                  placeholder={currentOrg.name}
                  value={orgName}
                  onChange={(e) => setOrgName(e.currentTarget.value)}
                />
                <Button type="submit" variant="default" disabled={!orgName.trim()}>
                  Rename
                </Button>
              </Group>
            </form>
          )}

          {/* Everyone can leave except the primary owner, who has to hand the
              organisation on first — it must always have a responsible party. */}
          {currentOrg.isPrimaryOwner ? (
            <Text c="dimmed" size="sm">
              You are the primary owner, so you can't leave this organisation. Transferring that
              role isn't built yet.
            </Text>
          ) : (
            <Group>
              <Button
                variant="light"
                color="red"
                onClick={() => {
                  if (!currentOrg) return
                  void leaveOrganisation(currentOrg.id, user.uid).then(() => navigate('/'))
                }}
              >
                Leave {currentOrg.name}
              </Button>
            </Group>
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}
